// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Lakko Contributors

/**
 * Route-level access guards.
 *
 *   - `<RequireSession>` — wraps protected routes. If the user is
 *      anonymous, redirect to the login page; if the auth context
 *      is still resolving, show a small loading placeholder.
 *
 *   - `<RequireAnonymous>` — wraps the login / signup screens.
 *      If the user is already signed in, redirect home so they do
 *      not see a stale form.
 *
 * Both guards are declarative wrappers — pass children through and
 * the consumer never has to think about navigation imperatively.
 *
 * What either guard renders is decided by `phrase-gate.ts`, which also holds
 * the rule that owed new recovery words, and the sign-up's "Protect your
 * account" step, are shown before ANY route.
 *
 * The guards read the real auth state behind `useAuth()`, which rehydrates
 * a persisted session on mount, so they see a genuine `loading` / `anon` /
 * `session` value.
 */
import { Match, Show, Switch, type JSX } from "solid-js";
import { Navigate, useLocation } from "@solidjs/router";
import { useAuth } from "./AuthContext";
import { paths } from "../routes/paths";
import { t } from "../l10n";
import RestoreIdentityPrompt from "./RestoreIdentityPrompt";
import SaveFreshPhrasePrompt from "./SaveFreshPhrasePrompt";
import SignupPhrasePrompt from "./SignupPhrasePrompt";
import ProtectAccountPrompt from "./protect/ProtectAccountPrompt";
import NewPasswordPrompt from "./NewPasswordPrompt";
import { RemovedDeviceScreen } from "../devices/RemovedDeviceScreen";
import { GuestAccountScreen } from "./GuestAccountOffer";
import { KeyStoreUnavailableScreen } from "./KeyStoreUnavailableScreen";
import { readReturnUrl } from "./return-url";
import {
  anonymousGuardView,
  sessionGuardView,
  type GuardView,
} from "./phrase-gate";

/** Gate every route inside the AppShell behind a real session. */
export function RequireSession(props: { children: JSX.Element }): JSX.Element {
  const auth = useAuth();
  const view = (): GuardView =>
    sessionGuardView(
      auth.state().kind,
      auth.signupPhrase() !== null,
      auth.newPasswordOffered(),
      auth.protectStepOwed(),
      auth.isGuest(),
    );
  return (
    <SignedOutGate>
      <Show when={view() === "children"} fallback={<GuardFallback view={view()} />}>
        {props.children}
      </Show>
    </SignedOutGate>
  );
}

/**
 * ADR-0073 (UX 15.2): while this device's keys are not the account's keys
 * any more (`auth.signedOut()`), the signed-out screen replaces every route,
 * public ones included, whatever the auth state says: the automatic wipe
 * moves the state to `anon` under it, and the screen must stay until the
 * person taps "Sign in". The dismissible "published identity changed"
 * banner that used to sit here is gone: a changed identity is either this
 * screen or nothing.
 */
function SignedOutGate(props: { children: JSX.Element }): JSX.Element {
  const auth = useAuth();
  return (
    <Show when={auth.signedOut()} fallback={<KeyStoreGate>{props.children}</KeyStoreGate>}>
      {(signedOut) => <RemovedDeviceScreen signedOut={signedOut()} />}
    </Show>
  );
}

/**
 * ADR-0085 (desktop, `docs/ux/desktop.md` 1.1): a sign-in or landing that
 * stopped because the OS key store refused shows the refusal screen in
 * place of every route, public ones included, until Try again gets past it.
 */
function KeyStoreGate(props: { children: JSX.Element }): JSX.Element {
  const auth = useAuth();
  return (
    <Show when={auth.keyStoreUnavailable()} fallback={props.children}>
      <KeyStoreUnavailableScreen />
    </Show>
  );
}

/** Mirror of `RequireSession` for the public auth pages. */
export function RequireAnonymous(props: {
  children: JSX.Element;
}): JSX.Element {
  const auth = useAuth();
  const view = (): GuardView =>
    anonymousGuardView(
      auth.state().kind,
      auth.signupPhrase() !== null,
      auth.newPasswordOffered(),
      auth.protectStepOwed(),
    );
  return (
    <SignedOutGate>
      <Show when={view() === "children"} fallback={<GuardFallback view={view()} />}>
        {props.children}
      </Show>
    </SignedOutGate>
  );
}

/**
 * Everything a guard renders instead of its route.
 *
 * - `loading`: a placeholder rather than an instant redirect — bouncing the
 *   user to /login during a half-second rehydration is jarring.
 * - `restore-identity` / `save-fresh-phrase`: ADR-0017 — an authenticated
 *   user whose identity is not yet on this device, or who just minted a new
 *   one, must not be bounced (that would loop).
 * - `choose-new-password`: the step after a forgot-password route on a
 *   device that holds the keys (docs/ux/identity-recovery.md 7.1). Saving or
 *   "Not now" clears it and the decision re-runs.
 * - `save-signup-phrase`: new recovery words (the account keys were changed
 *   on this device), until acknowledged.
 * - `protect-account`: the sign-up's "Protect your account" step (ADR-0075,
 *   UX 19.1a), until the person chose. Clearing it re-runs the decision: on
 *   a public auth page that is `redirect-home`, which honours `?return=`;
 *   inside the app the route underneath simply renders.
 * - `guest`: a guest account (`guest-account.ts`) never lands in the app; it
 *   is sent back to its call, or asked whether to keep the account.
 */
function GuardFallback(props: { view: GuardView }): JSX.Element {
  const location = useLocation();
  return (
    <Switch>
      <Match when={props.view === "loading"}>
        <p class="lk-muted" style={{ padding: "24px" }}>{t("common.loading")}</p>
      </Match>
      <Match when={props.view === "restore-identity"}>
        <RestoreIdentityPrompt />
      </Match>
      <Match when={props.view === "save-fresh-phrase"}>
        <SaveFreshPhrasePrompt />
      </Match>
      <Match when={props.view === "save-signup-phrase"}>
        <SignupPhrasePrompt />
      </Match>
      <Match when={props.view === "protect-account"}>
        <ProtectAccountPrompt />
      </Match>
      <Match when={props.view === "choose-new-password"}>
        <NewPasswordPrompt />
      </Match>
      <Match when={props.view === "guest"}>
        <GuestAccountScreen />
      </Match>
      <Match when={props.view === "redirect-login"}>
        <Navigate href={paths.login} />
      </Match>
      <Match when={props.view === "redirect-home"}>
        {/* A public auth page reached with `?return=<path>` (1.18e — an invite
            landing's "Log in" / "Sign up") continues there once signed in —
            including after the signup phrase is acknowledged. Validated
            path-only by `readReturnUrl`; `/` when absent or unsafe. */}
        <Navigate href={readReturnUrl(location)} />
      </Match>
    </Switch>
  );
}
